/**
 * User identity management for PostHog.
 *
 * Registers user identity fields as super properties so every event
 * automatically carries: user_id, tenant_id, association_code, role, user_type,
 * user display fields, membership_status, community_access, account_status, etc.
 *
 * Call identifyUser() once after login and whenever user/tenant data changes.
 * Call resetUser() on logout.
 */

import { posthog } from './posthog';

export interface UserIdentity {
  userId:            string | null;
  tenantId:          string | null;
  associationCode:   string | null;
  tenantName?:       string | null;
  tenantAcronym?:    string | null;
  role?:             string | null;
  analyticsRoleType?: string | null;
  userType?:         string | null;
  analyticsUserType?: string | null;
  displayName?:      string | null;
  firstName?:        string | null;
  lastName?:         string | null;
  email?:            string | null;
  integrationId?:    string | null;
  membershipStatus?: string | null;
  isMember?:         boolean;
  platformIsMember?: boolean;
  communityAccess?:  boolean;
  hasOrganization?:  boolean;
  accountStatus?:    string | null;
  integrationIsMember?: boolean;
}

export function identifyUser(identity: UserIdentity): void {
  if (typeof window === 'undefined') return;

  try {

  const {
    userId, tenantId, associationCode, tenantName, tenantAcronym,
    role, analyticsRoleType, userType, analyticsUserType,
    displayName, firstName, lastName, email, integrationId, membershipStatus,
    isMember, platformIsMember, communityAccess, hasOrganization, accountStatus,
    integrationIsMember,
  } = identity;

  const normalizedIsMember =
    isMember ??
    integrationIsMember ??
    platformIsMember ??
    membershipStatus === 'member';

  // Identify the user in PostHog (links distinct_id to userId)
  if (userId) {
    posthog.identify(userId, {
      name:               displayName ?? undefined,
      first_name:         firstName ?? undefined,
      last_name:          lastName ?? undefined,
      email:              email ?? undefined,
      role:               role ?? undefined,
      user_type:          userType ?? undefined,
      analytics_role_type: analyticsRoleType ?? undefined,
      analytics_user_type: analyticsUserType ?? undefined,
      integration_id:     integrationId ?? undefined,
      membership_status:  membershipStatus ?? undefined,
      is_member:          normalizedIsMember,
      platform_is_member: platformIsMember,
      community_access:   communityAccess,
      account_status:     accountStatus ?? undefined,
      integration_is_member: integrationIsMember,
    });
  }

  // Tenant identity is registered below as ordinary event properties. Do not
  // invoke PostHog group analytics here: Analytics V2 groups by tenant_id and
  // association_code, so the paid group-processing add-on is unnecessary.

  // Register super properties — attached to every subsequent event
  posthog.unregister('userId');
  posthog.unregister('tenantId');
  posthog.unregister('userType');
  posthog.unregister('membershipStatus');
  posthog.unregister('isMember');
  posthog.unregister('integrationId');
  posthog.unregister('user_display_name');
  posthog.unregister('user_first_name');
  posthog.unregister('user_last_name');
  posthog.unregister('user_email');

  const superProps: Record<string, unknown> = {};
  if (userId)           superProps.user_id          = userId;
  if (tenantId)         superProps.tenant_id        = tenantId;
  if (associationCode)  superProps.association_code = associationCode;
  if (tenantName)       superProps.tenant_name      = tenantName;
  if (tenantAcronym)    superProps.tenant_acronym   = tenantAcronym;
  if (role)             superProps.role             = role;
  if (analyticsRoleType) superProps.analytics_role_type = analyticsRoleType;
  if (userType)         superProps.user_type        = userType;
  if (analyticsUserType) superProps.analytics_user_type = analyticsUserType;
  if (displayName)      superProps.user_display_name = displayName;
  if (firstName)        superProps.user_first_name   = firstName;
  if (lastName)         superProps.user_last_name    = lastName;
  if (email)            superProps.user_email        = email;
  if (integrationId)    superProps.integration_id   = integrationId;
  if (membershipStatus) superProps.membership_status = membershipStatus;
  if (normalizedIsMember !== undefined) superProps.is_member       = normalizedIsMember;
  if (platformIsMember !== undefined) superProps.platform_is_member = platformIsMember;
  if (communityAccess !== undefined) superProps.community_access   = communityAccess;
  if (hasOrganization !== undefined) superProps.has_organization   = hasOrganization;
  if (accountStatus)    superProps.account_status   = accountStatus;
  if (integrationIsMember !== undefined) superProps.integration_is_member = integrationIsMember;

    posthog.register(superProps);
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[Analytics] Failed to identify user', error);
    }
  }
}

export function resetUser(): void {
  if (typeof window === 'undefined') return;
  try {
    posthog.reset();
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[Analytics] Failed to reset user', error);
    }
  }
}
